import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowLeft, Star, Award, MapPin, ShieldCheck, GraduationCap, Clock, CheckCircle2 } from 'lucide-react';

const TutorProfileV4 = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    const fetchTutor = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BACKEND_URL}/api/v4/public/tutors`);
        const data = await response.json();
        
        if (data.success) {
          const foundTutor = data.tutors.find(t => t.id === id);
          if (foundTutor) {
            setTutor(foundTutor);
          } else {
            setError('Tutor not found');
          }
        } else {
          setError('Failed to load tutor');
        }
      } catch (err) {
        setError('Failed to load tutor profile');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTutor();
  }, [id, BACKEND_URL]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="h-12 w-12 animate-spin" style={{color: '#006C69'}} />
      </div>
    );
  }

  if (error || !tutor) {
    return (
      <div className="min-h-screen bg-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <Card className="text-center py-12">
            <CardContent>
              <p className="text-xl text-gray-600 mb-6">{error || 'Tutor not found'}</p>
              <Button onClick={() => navigate('/tutors')} className="text-white transition-all" style={{background: '#B7BF72', color: '#000000'}}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#000000';
                  e.currentTarget.style.color = '#FFFFFF';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#B7BF72';
                  e.currentTarget.style.color = '#000000';
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to Tutors
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Pure Black Header */}
      <div className="text-white border-b shadow-lg" style={{background: '#000000'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Button 
            variant="outline" 
            onClick={() => navigate('/tutors')}
            className="text-white hover:bg-white/10 backdrop-blur-sm mb-4 transition-all"
            style={{borderColor: 'rgba(183, 191, 114, 0.30)'}}
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to All Tutors
          </Button>
        </div>
      </div>

      {/* Profile Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Profile Card */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tutor Header */}
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-4xl font-bold text-gray-900 mb-2">
                      {tutor.directory_display_name?.trim() || tutor.full_name}
                    </CardTitle>
                    <div className="flex flex-wrap gap-2 mt-4">
                      <Badge className="flex items-center gap-1 px-3 py-1 text-white border-0" style={{background: '#006C69'}}>
                        <ShieldCheck className="w-4 h-4" />
                        <span className="font-semibold">Verified Tutor</span>
                      </Badge>
                      <Badge variant="outline" className="font-medium px-3 py-1" style={{background: 'rgba(183, 191, 114, 0.10)', color: '#333333', borderColor: 'rgba(183, 191, 114, 0.30)'}}>
                        <Star className="w-4 h-4 mr-1" style={{fill: '#B7BF72', color: '#B7BF72'}} />
                        5+ Years Experience
                      </Badge>
                      <Badge variant="outline" className="font-medium px-3 py-1" style={{background: 'rgba(0, 108, 105, 0.10)', color: '#333333', borderColor: 'rgba(0, 108, 105, 0.30)'}}>
                        <Award className="w-4 h-4 mr-1" style={{color: '#006C69'}} />
                        Subject Specialist
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* About Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <GraduationCap className="w-6 h-6" style={{color: '#006C69'}} />
                  About This Tutor
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="prose max-w-none">
                  <p className="text-gray-700 leading-relaxed text-base">
                    {tutor.notes || tutor.bio || "Proven tutor specializing in helping students achieve top exam results and academic excellence. Expert in VCE/HSC preparation with a track record of improved grades, stronger exam performance, and university placement success. Focused on building confidence and delivering measurable results through strategic, personalized teaching methods."}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Specializations */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Specializations</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {tutor.subjects && tutor.subjects.map((subject, idx) => (
                    <Badge 
                      key={idx} 
                      className="border-0 px-4 py-2 text-base font-semibold"
                      style={{background: '#B7BF72', color: '#000000'}}
                    >
                      {subject}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Teaching Approach */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Teaching Approach</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 mt-1 flex-shrink-0" style={{color: '#006C69'}} />
                    <div>
                      <p className="font-semibold text-gray-900">Personalized Learning</p>
                      <p className="text-sm text-gray-600">Tailored lessons to match your learning style</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 mt-1 flex-shrink-0" style={{color: '#006C69'}} />
                    <div>
                      <p className="font-semibold text-gray-900">Exam Focused</p>
                      <p className="text-sm text-gray-600">Strategic preparation for top results</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 mt-1 flex-shrink-0" style={{color: '#006C69'}} />
                    <div>
                      <p className="font-semibold text-gray-900">Progress Tracking</p>
                      <p className="text-sm text-gray-600">Regular feedback and assessment</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 mt-1 flex-shrink-0" style={{color: '#006C69'}} />
                    <div>
                      <p className="font-semibold text-gray-900">Flexible Schedule</p>
                      <p className="text-sm text-gray-600">Sessions adapted to your availability</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Why Choose This Tutor */}
            <Card className="border-2" style={{background: 'rgba(183, 191, 114, 0.08)', borderColor: 'rgba(183, 191, 114, 0.25)'}}>
              <CardHeader>
                <CardTitle className="text-2xl" style={{color: '#000000'}}>Why Choose This Tutor?</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Star className="w-5 h-5 mt-1 flex-shrink-0" style={{color: '#B7BF72', fill: '#B7BF72'}} />
                    <span className="text-gray-700">Proven track record of student success and improved results</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Star className="w-5 h-5 mt-1 flex-shrink-0" style={{color: '#B7BF72', fill: '#B7BF72'}} />
                    <span className="text-gray-700">Expert knowledge in subject areas with current curriculum expertise</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Star className="w-5 h-5 mt-1 flex-shrink-0" style={{color: '#B7BF72', fill: '#B7BF72'}} />
                    <span className="text-gray-700">Patient, encouraging teaching style that builds student confidence</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Star className="w-5 h-5 mt-1 flex-shrink-0" style={{color: '#B7BF72', fill: '#B7BF72'}} />
                    <span className="text-gray-700">Flexible availability for both online and in-person sessions</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Booking Card */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6 border-2 shadow-xl">
              <CardContent className="p-6 space-y-6">
                {/* Availability */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5" style={{color: '#006C69'}} />
                    Availability
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg" style={{color: '#333333', background: 'rgba(183, 191, 114, 0.10)'}}>
                      <MapPin className="w-4 h-4" style={{color: '#006C69'}} />
                      <span className="font-medium">Online Sessions</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg" style={{color: '#333333', background: 'rgba(0, 108, 105, 0.10)'}}>
                      <MapPin className="w-4 h-4" style={{color: '#006C69'}} />
                      <span className="font-medium">In-Person (Melbourne)</span>
                    </div>
                  </div>
                </div>

                {/* Qualifications */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Award className="w-5 h-5" style={{color: '#006C69'}} />
                    Qualifications
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#006C69'}} />
                      <span>University Degree</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#006C69'}} />
                      <span>Subject Specialist</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#006C69'}} />
                      <span>Background Checked</span>
                    </li>
                  </ul>
                </div>

                <div className="border-t pt-6 space-y-4">
                  {/* CTA Button */}
                  <Button 
                    className="w-full font-semibold py-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 text-lg" 
                    style={{background: '#B7BF72', color: '#000000'}}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#000000';
                      e.currentTarget.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#B7BF72';
                      e.currentTarget.style.color = '#000000';
                    }}
                    onClick={() => navigate('/enquire-v4', { state: { tutorId: tutor.id, tutorName: tutor.full_name } })}
                  >
                    Book Your Free Consultation
                  </Button>

                  {/* Urgency Line */}
                  <div className="text-center">
                    <p className="text-sm font-medium" style={{color: '#B7BF72'}}>
                      ⏰ Limited spots available this week
                    </p>
                  </div>

                  {/* Platform Trust Line */}
                  <div className="flex items-center justify-center gap-2 text-xs py-3 px-3 rounded-md border" style={{color: '#333333', background: 'rgba(183, 191, 114, 0.08)', borderColor: 'rgba(183, 191, 114, 0.25)'}}>
                    <ShieldCheck className="w-4 h-4 flex-shrink-0" style={{color: '#006C69'}} />
                    <span className="font-medium text-center">All sessions managed securely through Winhouse Tutors</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorProfileV4;
